import React, { useState } from 'react';
import { Field, Formik } from 'formik';
import Fields from 'components/Fields';
import api from 'api';
import { BoxWrapper, Button, Prompt } from 'components/Base';
import * as Yup from 'yup';
import { Notify } from 'utils';
import Store from 'store/store';
import { connect } from 'react-redux';
import actions from 'store/actions';
import isEqual from 'lodash.isequal';
import { convert } from 'html-to-text';

interface DisclaimerProps {
  classes: any;
  disclaimer: any;
  setFirmDisclaimer: any;
  defaultDisclaimer: string;
}

const validationSchema = Yup.object().shape({
  disclaimer: Yup.string()
    .trim()
    .test('is-not-empty-html', 'Disclaimer is required', value => {
      if (!value) return false;
      const strippedText = value.replace(/<\/?[^>]+(>|$)/g, '').trim();
      return strippedText.length > 0;
    })
    .required('Disclaimer is required')
});

const { selectors } = Store;

const normalizeHTML = (html: string) =>
  convert(html, { wordwrap: false }).replace(/\s+/g, ' ').trim();

const Disclaimer = ({
  disclaimer,
  setFirmDisclaimer,
  classes,
  defaultDisclaimer
}: DisclaimerProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  const handleClear = (
    resetForm: (values: any) => void,
    submitForm: () => void
  ) => {
    setIsClearing(true);
    setFirmDisclaimer(defaultDisclaimer);
    resetForm({ values: { disclaimer: disclaimer } });
    submitForm();
    setIsClearModalOpen(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        disclaimer: disclaimer || defaultDisclaimer
      }}
      onSubmit={values => {
        setIsSaving(true);
        api
          .setFirmConfig({ disclaimer: isClearing ? null : values.disclaimer })
          .then(() => {
            setFirmDisclaimer(values.disclaimer);
            Notify.success('Disclaimer updated successfully.');
          })
          .catch(() => {
            Notify.error('Failed to update the disclaimer.');
          })
          .finally(() => {
            setIsClearing(false);
            setIsSaving(false);
          });
      }}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, resetForm, submitForm, values }) => {
        const isClearDisabled = isEqual(
          normalizeHTML(values.disclaimer),
          normalizeHTML(defaultDisclaimer)
        );

        return (
          <BoxWrapper
            title={'Adviser Disclaimer'}
            subTitle="Customise the disclaimer that appears at the end of all reports generated in Fintegrate. Use this to ensure regulatory compliance and align messaging with your firm's policies. Click 'Reset' to revert to the default disclaimer."
          >
            <form onSubmit={handleSubmit}>
              <div>
                <div
                  className={classes.formWrapper}
                  style={{ marginTop: '1rem', maxWidth: '90%' }}
                >
                  <Field
                    style={{ background: '#fafafa' }}
                    name="disclaimer"
                    component={Fields.v2.WYSIWYGEditor}
                    filled
                  />
                </div>
              </div>
              <div>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={isSaving}
                  isLoading={isSaving}
                  style={{ margin: '1rem' }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setIsClearModalOpen(true)}
                  disabled={isSaving || isClearDisabled}
                  style={{ margin: '1rem' }}
                >
                  Reset
                </Button>
              </div>
            </form>

            <Prompt
              isOpen={isClearModalOpen}
              title="Are you sure you want to reset the disclaimer to the default?"
              isProcessing={isSaving}
              confirmAction={() => handleClear(resetForm, submitForm)}
              cancelAction={() => setIsClearModalOpen(false)}
            />
          </BoxWrapper>
        );
      }}
    </Formik>
  );
};

export default connect(
  (state: any) => ({
    disclaimer: selectors.firmSelectors.disclaimer(state),
    defaultDisclaimer: selectors.firmSelectors.defaultDisclaimer(state)
  }),
  {
    setFirmDisclaimer: actions.firmActions.setDisclaimer
  }
)(Disclaimer);
